import { useQuery } from '@tanstack/react-query';
import type { CollectionDefaultBenchmarkView, CollectionScoringSummaryView } from '@vizit/api';
import { useGetCollectionScoringQuery } from '@vizit/api';
import type { GetProductCategoriesQueryVariables, ImageType } from '@vizit/api';
import { UNIVERSAL_BENCHMARK_KEY } from '@vizit/constants';
import { useShowUniversalBenchmarks } from '@vizit/feature-flags';
import { type BenchmarkOption, getBenchmarkOptionKey, isVizitRetailer, sortPickerCategories } from '@vizit/organisms';
import { getBenchmarkImagesNeeded } from '@vizit/organisms';
import * as React from 'react';
import { type UnscoredImagesReport, isSamePair } from '../components/collection-asset-grid/collection-asset-grid.state';
import { addCategoriesErrorMessage } from './collection-scoring-copy';
import type { ObservedScoring } from './collection-scoring-notices';
import { nameRefusedCategories, nameScorings } from './scoring-category-names';
import { useFindProductCategory, useProductCategorySearch } from './use-benchmark-product-categories';
import { useScoredProductCategories } from './use-benchmark-product-categories';
import { toPickerCategory } from './use-benchmark-scorings';
import { useCollectionBenchmarkOptions } from './use-collection-benchmark-options';
import { useCollectionScoringNotices } from './use-collection-scoring-notices';
import { type CollectionScoringBenchmark, useCollectionScorings } from './use-collection-scorings';
import { toCollectionBenchmarkRef } from './use-collection-scorings';
import type { CollectionUploadRescore } from './use-collection-scorings.polling';

/** The picker key of the benchmark a collection was last scored against: a Vizit pair by retailer, a custom one by id. */
function toLastScoringBenchmarkKey(lastScoring: CollectionScoringSummaryView | null | undefined): string | null {
  if (!lastScoring) {
    return null;
  }
  return lastScoring.retailer ? `vizit:${lastScoring.retailer}` : `custom:${lastScoring.benchmark_id}`;
}

/** The picker key of the benchmark the collection was created to score against. */
function toDefaultBenchmarkKey(defaultBenchmark: CollectionDefaultBenchmarkView | null | undefined): string | null {
  if (defaultBenchmark?.retailer) {
    return `vizit:${defaultBenchmark.retailer}`;
  }
  return defaultBenchmark?.benchmark_id ? `custom:${defaultBenchmark.benchmark_id}` : null;
}

/** The benchmark a picker key names, enough to list its scorings before the picker's options have loaded. */
function toScoringBenchmark(key: string | null): CollectionScoringBenchmark | null {
  const [kind, value] = key?.split(/:(.*)/s) ?? [];
  if (!value) {
    return null;
  }
  if (kind === 'vizit') {
    return isVizitRetailer(value) ? { kind: 'vizit', retailer: value } : null;
  }
  return kind === 'custom' ? { kind: 'custom', id: value } : null;
}

/**
 * The filters that list only the categories a benchmark can score: a custom one by ID, a Vizit one by retailer for the
 * collection's image type, which the API requires with it. `null` until both are known; the dialog's list waits for
 * them rather than listing categories the benchmark may not score.
 */
function toScorableCategoryFilters(
  benchmark: CollectionScoringBenchmark | null,
  imageType: ImageType | undefined,
): Partial<GetProductCategoriesQueryVariables> | null {
  const ref = toCollectionBenchmarkRef(benchmark);
  if (ref?.benchmark_id) {
    return { scorable_for_benchmark_id: ref.benchmark_id };
  }
  return ref?.retailer && imageType ? { scorable_for_retailer: ref.retailer, scope: imageType } : null;
}

interface UseCollectionScoringControlsProps {
  workspaceId?: string;
  collectionId: string;
  /** The collection's image type; custom benchmarks of this type are offered */
  imageType?: ImageType;
  /** The pair the collection was last scored against, from the collection itself; the page opens on it */
  lastScoring?: CollectionScoringSummaryView | null;
  /** The benchmark the collection was created to score against; the page opens on it until a pair is scored */
  defaultBenchmark?: CollectionDefaultBenchmarkView | null;
  /** The collection's own product category, selected under a benchmark where none was picked, if scored there */
  collectionCategoryId?: string | null;
  /** Whether the collection has not loaded yet; its last scored pair is unknown, so no fallback is recorded */
  isCollectionPending?: boolean;
  /** Whether the collection failed its first load; its own category is unknown, so the grid is not told it needs one */
  isCollectionError?: boolean;
  /** Reads nothing while false, e.g. while the feature is off; hooks cannot be called conditionally */
  enabled?: boolean;
  /** The images the grid shows "Unscored" and the pair it read them under; named once the selected scoring settles */
  unscoredImages?: UnscoredImagesReport | null;
  /**
   * The benchmark the user picked that the collection has not recorded, as a picker key the page keeps, e.g. in its
   * URL. A pick with no category under it records nothing on the API, so without this a reload or a return to the page
   * reopens on the last scored pair. A change to it (the browser's Back or Forward) moves the selection with it.
   */
  pickedBenchmarkKey?: string | null;
  /**
   * Called with a picked benchmark the collection cannot record (no category under it, or under its minimum) once its
   * categories are known, for the page to keep. Called with `null` when a pick replaces the kept one, or once the kept
   * one is not offered (deleted, another image type, or not a benchmark at all) or the last scored pair is under it.
   */
  onPickedBenchmarkKeyChange?: (key: string | null) => void;
}

/**
 * What the collection page's Scoring Controls need: the benchmarks to pick from and the one picked, the product
 * categories the collection is scored under against it and the one picked, and adding categories.
 *
 * The page opens on the collection's last scored pair. Without one it opens on the benchmark the collection was
 * created with, and without that on the first benchmark that can score, Vizit first. Picking a benchmark shows the
 * last scored pair's category when it is under that benchmark, else the category last selected under it, else the
 * collection's own when it is scored there, else the picker's first row: a
 * collection is never scored without a category, so one is always selected while there are any. That pair is recorded
 * as the collection's selection once its list has loaded, so a reload reopens there. Picking a category records it.
 * A picked benchmark is also handed to the page to keep, as nothing is recorded under one without categories; the page
 * reopens on the kept one until the collection's last scored pair is under it.
 * Adding categories requests a scoring for each, selects the first one the API accepted and records it, as the
 * benchmark page does.
 */
function useCollectionScoringControls({
  workspaceId,
  collectionId,
  imageType,
  lastScoring,
  defaultBenchmark,
  collectionCategoryId = null,
  isCollectionPending = false,
  isCollectionError = false,
  enabled = true,
  unscoredImages = null,
  pickedBenchmarkKey: keptBenchmarkKey = null,
  onPickedBenchmarkKeyChange,
}: UseCollectionScoringControlsProps) {
  const showUniversalBenchmarks = useShowUniversalBenchmarks();
  const {
    options,
    isLoading: isOptionsLoading,
    isError: isOptionsError,
    retry: retryOptions,
  } = useCollectionBenchmarkOptions({
    workspaceId,
    collectionId,
    scope: imageType,
    enabled,
  });

  // `undefined` means the user has not picked yet, so the page follows the collection's last scored pair.
  const [pickedBenchmarkKey, setPickedBenchmarkKey] = React.useState<string | undefined>(undefined);
  // What this hook last wrote to the URL, so its own write is not taken for the browser's Back or Forward.
  const [writtenKeptKey, setWrittenKeptKey] = React.useState<string | null | undefined>(undefined);
  const writeKeptKey = React.useCallback(
    (key: string | null) => {
      setWrittenKeptKey(key);
      onPickedBenchmarkKeyChange?.(key);
    },
    [onPickedBenchmarkKeyChange],
  );
  // The kept pick leads once it changes, e.g. on the browser's Back, over a pick made on the page since.
  const [previousKeptKey, setPreviousKeptKey] = React.useState(keptBenchmarkKey);
  if (previousKeptKey !== keptBenchmarkKey) {
    setPreviousKeptKey(keptBenchmarkKey);
    if (keptBenchmarkKey === writtenKeptKey) {
      setWrittenKeptKey(undefined);
    } else {
      setPickedBenchmarkKey(undefined);
    }
  }
  const firstEnabledKey = options.find((option) => !option.disabled);
  const fallbackKey = firstEnabledKey ? getBenchmarkOptionKey(firstEnabledKey) : null;
  const isListed = (key: string) => options.some((option) => getBenchmarkOptionKey(option) === key);
  // The page opens on the last scored pair, else the collection's default benchmark, else the first one that can
  // score. One no longer offered (deleted, or another type) gives way to the next once the options are known, and so
  // does a pick the picker stopped listing, e.g. Universal when its flag turns off mid-session.
  const isOffered = (key: string | null | undefined): key is string => !!key && (isOptionsLoading || isListed(key));
  const openingKey = [toLastScoringBenchmarkKey(lastScoring), toDefaultBenchmarkKey(defaultBenchmark)].find(isOffered);
  // A kept pick is followed like the last scored pair: while the options load, or once they list it.
  const keptKey =
    keptBenchmarkKey && toScoringBenchmark(keptBenchmarkKey) && isOffered(keptBenchmarkKey)
      ? keptBenchmarkKey
      : undefined;
  const selectedBenchmarkKey =
    (isOffered(pickedBenchmarkKey) ? pickedBenchmarkKey : undefined) ?? keptKey ?? openingKey ?? fallbackKey;
  const benchmark = React.useMemo(
    () => (enabled ? toScoringBenchmark(selectedBenchmarkKey) : null),
    [enabled, selectedBenchmarkKey],
  );
  const selectedBenchmark = options.find((option) => getBenchmarkOptionKey(option) === selectedBenchmarkKey) ?? null;
  // A selected set can drop under its minimum after it was picked (images removed); the API then scores nothing new
  // against it, so the bar says why and nothing offers to add a category.
  const imagesNeeded = selectedBenchmark ? getBenchmarkImagesNeeded(selectedBenchmark) : 0;
  const isUnderMinimum = imagesNeeded > 0;
  const underMinimumBenchmark =
    selectedBenchmark && isUnderMinimum ? { name: selectedBenchmark.name, imagesNeeded } : null;

  // The upload the list on screen waits on, mirrored from the notices below: they need this list to observe.
  const [listUpload, setListUpload] = React.useState<CollectionUploadRescore | null>(null);

  const {
    scored,
    processing,
    failed,
    progress,
    lastSelected,
    isLoading: isScoringsLoading,
    isResolved: areScoringsResolved,
    isError: isScoringsError,
    requestScorings,
    recordSelection: saveSelection,
    refreshImages,
    retry: retryScorings,
  } = useCollectionScorings({ workspaceId, collectionId, benchmark, upload: listUpload });
  // Under its minimum the API refuses to record a pick (400), so a switch, a fallback or "View scores" stays on the page.
  // Its minimum is only known once the picker's options load, so nothing is recorded before then either.
  const canRecordSelection = !!selectedBenchmark && !isUnderMinimum;
  const recordSelection = React.useCallback<typeof saveSelection>(
    (...args) => {
      if (canRecordSelection) {
        saveSelection(...args);
      }
    },
    [canRecordSelection, saveSelection],
  );
  // Exactly what the picker lists; a scoring in no list cannot be the selection.
  const listedScorings = React.useMemo(() => [...scored, ...processing, ...failed], [scored, processing, failed]);
  const hasCategories = listedScorings.length > 0;
  // A Vizit benchmark without a pair keeps the images' own scores, which are ranked against Vizit benchmarks too.
  const isCustomBenchmark = benchmark?.kind === 'custom';
  const isAwaitingCategory = isCustomBenchmark && areScoringsResolved && !isScoringsError && !hasCategories;

  const [isAddCategoriesOpen, setIsAddCategoriesOpen] = React.useState(false);
  // Nothing can be added under the minimum, so a dialog open when the benchmark drops below it closes.
  const isAddCategoriesShown = isAddCategoriesOpen && !isUnderMinimum;
  const [addCategoriesError, setAddCategoriesError] = React.useState<string | null>(null);
  // The listed categories name and color the picker rows and give their paths; the dialog searches its own list, the
  // ones the picked benchmark can score.
  const { byId: categoryById, status: categoryPathsStatus } = useScoredProductCategories({
    workspaceId,
    scorings: listedScorings,
    enabled,
  });
  const scorableFilters = React.useMemo(() => toScorableCategoryFilters(benchmark, imageType), [benchmark, imageType]);
  const categorySearch = useProductCategorySearch({
    workspaceId,
    filters: scorableFilters,
    enabled: enabled && isAddCategoriesShown,
  });

  const findCategory = useFindProductCategory(categoryById, categorySearch.categories);

  // The picker's rows name the notices, the inline status and the upload note, so each reads like its row.
  const { toPicker, nameOf } = React.useMemo(
    () => nameScorings(listedScorings, findCategory),
    [listedScorings, findCategory],
  );
  const excludedCategoryIds = React.useMemo(
    () => [...scored, ...processing].map((scoring) => scoring.productCategoryId),
    [scored, processing],
  );

  const pickerRows = React.useMemo(() => {
    const rows = { scored: scored.map(toPicker), processing: processing.map(toPicker), failed: failed.map(toPicker) };
    // The picker lists each group by name; its first row is the selection of last resort.
    const first = [rows.scored, rows.processing, rows.failed]
      .map((group) => sortPickerCategories(group)[0])
      .find(Boolean);
    return { ...rows, firstId: first?.id ?? null };
  }, [scored, processing, failed, toPicker]);

  // A category pick belongs to the benchmark it was made under; switching benchmarks drops it.
  const [pickedCategory, setPickedCategory] = React.useState<{ benchmarkKey: string | null; id: string | null }>();
  const pickedCategoryId = pickedCategory?.benchmarkKey === selectedBenchmarkKey ? pickedCategory.id : undefined;
  const isCategoryListed = (categoryId: string | null | undefined) =>
    !!categoryId && listedScorings.some((scoring) => scoring.productCategoryId === categoryId);
  // A category is always selected while there are any: the one picked, else the collection's last scored pair's when
  // under this benchmark, else the one last selected under the benchmark, while it is still listed; else the
  // collection's own when it is scored there; else the picker's first row.
  const lastScoringCategoryId =
    toLastScoringBenchmarkKey(lastScoring) === selectedBenchmarkKey ? lastScoring?.product_category_id : undefined;
  const rememberedCategoryId = [lastScoringCategoryId, lastSelected?.productCategoryId].find(isCategoryListed);
  const chosenCategoryId = pickedCategoryId === undefined ? rememberedCategoryId : pickedCategoryId;
  // While the listing is unread or failed, the last scored pair stands in so the picker is never empty; never recorded.
  const provisionalScoring =
    !hasCategories && (!areScoringsResolved || isScoringsError) && lastScoring && lastScoringCategoryId
      ? lastScoring
      : null;
  const provisionalCategory = React.useMemo(
    () =>
      provisionalScoring
        ? toPickerCategory(
            {
              productCategoryId: provisionalScoring.product_category_id,
              productCategoryName: provisionalScoring.product_category_name,
            },
            findCategory(provisionalScoring.product_category_id),
          )
        : null,
    [provisionalScoring, findCategory],
  );
  const fallbackCategoryId = isCategoryListed(collectionCategoryId)
    ? (collectionCategoryId ?? null)
    : pickerRows.firstId;
  const selectedCategoryId = isCategoryListed(chosenCategoryId) ? (chosenCategoryId ?? null) : fallbackCategoryId;
  const selectedScoring = listedScorings.find((scoring) => scoring.productCategoryId === selectedCategoryId) ?? null;
  const isFallbackSelection = !!selectedScoring && selectedCategoryId !== chosenCategoryId;

  // The kept pick gives way to the collection's own state once that is under it, and is dropped silently when no
  // longer offered; the page then opens as it would without one. While the options fail it may yet be offered.
  const lastScoringKey = toLastScoringBenchmarkKey(lastScoring);
  const isKeptKeyDone =
    enabled &&
    !!keptBenchmarkKey &&
    (keptBenchmarkKey === lastScoringKey || (!isOptionsLoading && !isOptionsError && !keptKey));
  React.useEffect(() => {
    if (isKeptKeyDone) {
      onPickedBenchmarkKeyChange?.(null);
    }
  }, [isKeptKeyDone, onPickedBenchmarkKeyChange]);

  // Set when the user switches benchmarks; the pair shown once the new list loads is then recorded.
  const [pendingRecordKey, setPendingRecordKey] = React.useState<string | null>(null);
  const [viewedBenchmarkKey, setViewedBenchmarkKey] = React.useState<string | null>(null);
  const isViewingOnly = !!viewedBenchmarkKey && viewedBenchmarkKey === selectedBenchmarkKey;
  // A pick the picker stopped listing before its list loaded records nothing, so the page's own fallback can.
  const isPendingRecordUnoffered = !!pendingRecordKey && !isOffered(pendingRecordKey);
  React.useEffect(() => {
    if (isPendingRecordUnoffered) {
      setPendingRecordKey(null);
    }
  }, [isPendingRecordUnoffered]);
  React.useEffect(() => {
    if (!pendingRecordKey || pendingRecordKey !== selectedBenchmarkKey || !areScoringsResolved) {
      return;
    }
    setPendingRecordKey(null);
    // Only a pick the collection cannot record (no category yet, or under the minimum) is kept in the URL; the
    // collection's own record keeps the rest.
    if ((!selectedScoring || !canRecordSelection) && pendingRecordKey !== lastScoringKey) {
      writeKeptKey(pendingRecordKey);
    }
    if (selectedScoring) {
      recordSelection(selectedScoring.productCategoryId);
    } else if (isAwaitingCategory && scorableFilters && !isUnderMinimum) {
      // A pick of a benchmark with nothing to score against asks for a category once, not on every load.
      setAddCategoriesError(null);
      setIsAddCategoriesOpen(true);
    }
  }, [
    pendingRecordKey,
    selectedBenchmarkKey,
    areScoringsResolved,
    selectedScoring,
    recordSelection,
    isAwaitingCategory,
    scorableFilters,
    isUnderMinimum,
    canRecordSelection,
    lastScoringKey,
    writeKeptKey,
  ]);

  // A selection the page fell back to is recorded once its list has loaded, so it stays put when another category
  // lands later and a reload reopens there. Not when the last pair is Universal hidden only by this user's flag: the
  // pair is still valid, and recording would move it for everyone.
  const isLastPairHiddenByFlag = !showUniversalBenchmarks && lastScoring?.retailer === UNIVERSAL_BENCHMARK_KEY;
  const recordedFallbackRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    // Not marked as recorded while nothing can be sent, so it records once the benchmark can score again.
    if (
      !isFallbackSelection ||
      isLastPairHiddenByFlag ||
      isCollectionPending ||
      !areScoringsResolved ||
      pendingRecordKey ||
      !selectedScoring ||
      isViewingOnly ||
      !canRecordSelection
    ) {
      return;
    }
    const key = `${selectedBenchmarkKey}:${selectedScoring.productCategoryId}`;
    if (recordedFallbackRef.current === key) {
      return;
    }
    recordedFallbackRef.current = key;
    recordSelection(selectedScoring.productCategoryId);
  }, [
    isFallbackSelection,
    isLastPairHiddenByFlag,
    isCollectionPending,
    areScoringsResolved,
    pendingRecordKey,
    selectedScoring,
    selectedBenchmarkKey,
    recordSelection,
    isViewingOnly,
    canRecordSelection,
  ]);

  const selectBenchmark = React.useCallback(
    (option: BenchmarkOption) => {
      const key = getBenchmarkOptionKey(option);
      if (key === selectedBenchmarkKey) {
        return;
      }
      setPickedBenchmarkKey(key);
      setPickedCategory(undefined);
      setPendingRecordKey(key);
      setViewedBenchmarkKey(null);
      // A benchmark kept from before would reopen on reload over this pick; this one is kept once it cannot record.
      if (keptBenchmarkKey) {
        writeKeptKey(null);
      }
    },
    [selectedBenchmarkKey, keptBenchmarkKey, writeKeptKey],
  );

  const viewBenchmark = React.useCallback((option: BenchmarkOption) => {
    const key = getBenchmarkOptionKey(option);
    setPickedBenchmarkKey(key);
    setPickedCategory((picked) => (picked?.benchmarkKey === key ? picked : undefined));
    setViewedBenchmarkKey(key);
  }, []);

  const selectCategory = React.useCallback(
    (categoryId: string | null) => {
      setPickedCategory({ benchmarkKey: selectedBenchmarkKey, id: categoryId });
      if (categoryId) {
        recordSelection(categoryId);
      }
    },
    [selectedBenchmarkKey, recordSelection],
  );

  /** Shows a category that just landed, switching back to its benchmark when the user has moved on since. */
  const viewScores = React.useCallback(
    (benchmarkKey: string | null, categoryId: string) => {
      setPickedCategory({ benchmarkKey, id: categoryId });
      if (benchmarkKey === selectedBenchmarkKey) {
        recordSelection(categoryId);
        return;
      }
      if (benchmarkKey) {
        setPickedBenchmarkKey(benchmarkKey);
        setPendingRecordKey(benchmarkKey);
        if (keptBenchmarkKey) {
          writeKeptKey(null);
        }
      }
    },
    [selectedBenchmarkKey, recordSelection, keptBenchmarkKey, writeKeptKey],
  );
  const benchmarkRef = toCollectionBenchmarkRef(benchmark);
  const pairBenchmarkId = benchmarkRef?.benchmark_id ?? null;
  const pairRetailer = benchmarkRef?.retailer ?? null;
  // A failed listing leaves the grid on the last scored pair, as the picker shows it.
  const fallbackPairScoring = isScoringsError ? provisionalScoring : null;
  const pairCategoryId = selectedScoring?.productCategoryId ?? fallbackPairScoring?.product_category_id ?? null;
  const pairRequestedAt = selectedScoring?.requestedAt ?? fallbackPairScoring?.requested_at ?? null;
  // Without any benchmark to offer (the options failed or are empty) there is nothing to wait for.
  const isPairResolved = areScoringsResolved || (!benchmark && !isOptionsLoading);
  const pairScoring = React.useMemo(
    () => ({
      pair: pairCategoryId
        ? { product_category_id: pairCategoryId, benchmark_id: pairBenchmarkId, retailer: pairRetailer }
        : null,
      isResolved: isPairResolved,
      requestedAt: pairRequestedAt,
    }),
    [pairCategoryId, pairBenchmarkId, pairRetailer, isPairResolved, pairRequestedAt],
  );

  const observedScorings = React.useMemo<ObservedScoring[]>(
    () =>
      listedScorings.map((scoring) => ({
        id: scoring.id,
        categoryId: scoring.productCategoryId,
        name: nameOf(scoring),
        status: scoring.status,
        requestedAt: scoring.requestedAt,
      })),
    [listedScorings, nameOf],
  );
  const notices = useCollectionScoringNotices({
    workspaceId,
    collectionId,
    benchmarkKey: enabled ? selectedBenchmarkKey : null,
    scorings: observedScorings,
    isResolved: areScoringsResolved,
    pair: pairScoring.pair,
    onViewScores: viewScores,
  });
  React.useEffect(() => {
    setListUpload(notices.upload);
  }, [notices.upload]);
  // Nothing to score against: no category under the benchmark and, under a Vizit one, none of the collection's own.
  const needsCategory =
    areScoringsResolved &&
    !isScoringsError &&
    !hasCategories &&
    (isCustomBenchmark || (!isCollectionPending && !isCollectionError && !collectionCategoryId));
  // The grid also dims the images of an open upload that have no score under the pair yet.
  const gridScoring = React.useMemo(
    () => ({ ...pairScoring, waitingImageIds: notices.waitingImageIds, isCustomBenchmark, needsCategory }),
    [pairScoring, notices.waitingImageIds, isCustomBenchmark, needsCategory],
  );

  const openAddCategories = React.useCallback(() => {
    setAddCategoriesError(null);
    setIsAddCategoriesOpen(true);
  }, []);

  const addCategories = React.useCallback(
    async (categoryIds: string[]) => {
      setAddCategoriesError(null);
      const { requested, failedCategoryIds, failureCodes } = await requestScorings(categoryIds);
      // The first category that got through becomes the selection, scoring or already scored, and is recorded as
      // such: every request stamps a selection, so without this a reload would reopen on whichever landed last.
      const first = categoryIds
        .map((id) => requested.find((scoring) => scoring.productCategoryId === id))
        .find(Boolean);
      if (first) {
        setPickedCategory({ benchmarkKey: selectedBenchmarkKey, id: first.productCategoryId });
        recordSelection(first.productCategoryId, first);
      }
      // Already scored categories answer ready at once; only the ones now scoring are announced and tracked. They
      // are named among the picker's rows, which they join.
      const { nameOf: nameRequested } = nameScorings(
        [
          ...listedScorings.filter(
            (listed) => !requested.some((scoring) => scoring.productCategoryId === listed.productCategoryId),
          ),
          ...requested,
        ],
        findCategory,
      );
      notices.trackRequested(
        requested.map((scoring) => ({
          id: scoring.id,
          categoryId: scoring.productCategoryId,
          name: nameRequested(scoring),
          status: scoring.status,
          requestedAt: scoring.requestedAt,
        })),
      );
      if (failedCategoryIds.length > 0) {
        const names = nameRefusedCategories(failedCategoryIds, {
          listed: categorySearch.categories,
          excludedIds: excludedCategoryIds,
          findCategory,
        });
        setAddCategoriesError(
          addCategoriesErrorMessage(
            failedCategoryIds.map((id, index) => ({
              name: names[index] ?? 'This category',
              errorCode: failureCodes?.[id],
            })),
            { name: selectedBenchmark?.name ?? 'This benchmark', isVizit: benchmark?.kind === 'vizit' },
          ),
        );
      }
      return { requested, failedCategoryIds };
    },
    [
      requestScorings,
      recordSelection,
      selectedBenchmarkKey,
      findCategory,
      notices.trackRequested,
      listedScorings,
      categorySearch.categories,
      excludedCategoryIds,
      selectedBenchmark?.name,
      benchmark?.kind,
    ],
  );

  const selectedCategoryName = selectedScoring ? nameOf(selectedScoring) : null;
  const selectedProgress = selectedScoring ? progress.get(selectedScoring.productCategoryId) : undefined;

  // A run scores image by image, and one that never finishes still has most of them scored: each time the selected
  // run has scored more than the page saw, the images are read again so the ready scores show while the rest stay
  // dimmed. A run selected with its count known (a category switch) starts from that count, as the grid reads its pair
  // then; one selected before its first count (just requested, or the page just opened) starts from none, as the grid
  // may have read it before that count. A run that stays put, e.g. on files it cannot read, costs no further reads.
  const seenScoredRef = React.useRef<{ runKey: string; scoredImages: number | null } | null>(null);
  React.useEffect(() => {
    if (selectedScoring?.status !== 'pending') {
      seenScoredRef.current = null;
      return;
    }
    const runKey = `${selectedScoring.id}:${selectedScoring.requestedAt}`;
    const scoredImages =
      selectedProgress?.status === 'pending' && selectedProgress.requestedAt === selectedScoring.requestedAt
        ? selectedProgress.scoredImages
        : null;
    const seen = seenScoredRef.current?.runKey === runKey ? seenScoredRef.current : null;
    seenScoredRef.current = { runKey, scoredImages: scoredImages ?? seen?.scoredImages ?? null };
    if (seen && scoredImages !== null && scoredImages > (seen.scoredImages ?? 0)) {
      void refreshImages();
    }
  }, [selectedScoring, selectedProgress, refreshImages]);

  // A landed scoring is no longer polled, so the selected one's status is read once more when it settled: why it
  // failed, how many images it left unscored, and which ones the API could not read. Keyed as its poll is, and read
  // afresh: the list can turn settled before the poll does, which then stops with the run cached as pending.
  const isSelectedSettled = selectedScoring?.status === 'ready' || selectedScoring?.status === 'failed';
  const selectedStatusVariables = {
    workspace_id: workspaceId ?? '',
    collection_id: collectionId,
    scoring_id: selectedScoring?.id ?? '',
  };
  const { data: selectedStatus } = useQuery({
    queryKey: [...useGetCollectionScoringQuery.getKey(selectedStatusVariables), selectedScoring?.requestedAt],
    queryFn: useGetCollectionScoringQuery.fetcher(selectedStatusVariables),
    enabled: enabled && !!workspaceId && isSelectedSettled,
    staleTime: 0,
  });
  const settledStatus = isSelectedSettled && selectedStatus?.status === selectedScoring?.status ? selectedStatus : null;

  // The unscored tiles the grid reported for the pair on screen, the ones the API could not read when it names them,
  // which are still counted when none is on screen; a report for another pair, e.g. the category before a switch,
  // names nothing.
  const gridPair = pairScoring.pair;
  const unscoredImageNames = React.useMemo(() => {
    if (!isSelectedSettled || !unscoredImages || !isSamePair(unscoredImages.pair, gridPair)) {
      return null;
    }
    const failedIds = settledStatus?.failedImageIds ?? [];
    const images = failedIds.length
      ? unscoredImages.images.filter((image) => failedIds.includes(image.id))
      : unscoredImages.images;
    if (images.length === 0 && failedIds.length === 0) {
      return null;
    }
    const count = failedIds.length || (settledStatus ? settledStatus.totalImages - settledStatus.scoredImages : null);
    return { names: images.map((image) => image.name), count };
  }, [isSelectedSettled, unscoredImages, gridPair, settledStatus]);

  return {
    /** The benchmarks the picker lists */
    options,
    isOptionsLoading,
    /** Whether the benchmarks failed to load; nothing is then selected, so the category controls stay disabled */
    isOptionsError,
    /** Reads the benchmarks again after a failure */
    retryOptions,
    selectedBenchmarkKey,
    /** The picked benchmark as the picker lists it; `null` until the options have loaded */
    selectedBenchmark,
    /** The picked benchmark as the scoring routes address it */
    benchmark,
    selectBenchmark,
    viewBenchmark,
    /** The scored rows, or the last scored pair's alone while the listing is unread or failed */
    scoredCategories: provisionalCategory ? [provisionalCategory] : pickerRows.scored,
    processingCategories: pickerRows.processing,
    failedCategories: pickerRows.failed,
    /** Where the rows' paths stand, as the picker's `pathsStatus` takes it */
    categoryPathsStatus,
    hasCategories: hasCategories || !!provisionalCategory,
    isScoringsLoading,
    areScoringsResolved,
    /** The picked category while a scoring under the benchmark backs it, else the last scored pair's stand-in */
    selectedCategoryId: selectedScoring ? selectedCategoryId : (provisionalCategory?.id ?? null),
    selectedScoring,
    selectedCategoryName,
    /** Live progress of the selected scoring while it is pending */
    selectedProgress,
    /** The inline status of the selected scoring, as `ScoringStatus` takes it */
    status: {
      scoringCategoryName: selectedScoring?.status === 'pending' ? selectedCategoryName : null,
      scoringProgress:
        selectedProgress?.status === 'pending'
          ? { scored: selectedProgress.scoredImages, total: selectedProgress.totalImages }
          : null,
      failedScoring:
        selectedScoring?.status === 'failed' && selectedCategoryName
          ? { categoryName: selectedCategoryName, reason: settledStatus?.failureReason }
          : null,
      unscoredImages: unscoredImageNames,
      underMinimumBenchmark,
    },
    /** The pair the grid's tiles are scored against, stable while it does not change */
    gridScoring,
    /** The categories new uploads are scored against under the benchmark, for the upload dialog's note */
    uploadCategoryNames: listedScorings.map(nameOf),
    trackUpload: notices.trackUpload,
    /** Adds an uploaded image to the upload being watched, once its record exists */
    trackUploadedImage: notices.trackUploadedImage,
    /** Takes a failed upload out of the upload being watched */
    trackFailedImage: notices.trackFailedImage,
    selectCategory,
    /** A custom benchmark is picked and its list read, with no category under it: nothing to score against yet */
    isAwaitingCategory,
    /**
     * The picked custom benchmark while the collection has no category under it, or none known yet (its list unread or
     * failed), for an asset opened from the page: it then checks for itself rather than show another benchmark's score.
     */
    awaitingCategoryBenchmarkKey:
      isCustomBenchmark && (!areScoringsResolved || isScoringsError || !hasCategories) ? selectedBenchmarkKey : null,
    /** Whether the picked custom benchmark's categories failed to load; the bar then offers to read them again */
    isScoringsError: isCustomBenchmark && isScoringsError,
    retryScorings,
    isAddCategoriesOpen: isAddCategoriesShown,
    setIsAddCategoriesOpen,
    /**
     * Whether categories can be added: a benchmark is picked, has the images it needs, and its scorable filters are
     * known (for a Vizit one, the collection's image type). Without them the dialog would wait on its list with no way
     * out, so it stays closed.
     */
    canAddCategories: !!benchmark && !underMinimumBenchmark && !!scorableFilters,
    openAddCategories,
    addCategoriesError,
    /** The categories the picked benchmark can score that match the dialog's search; none before a search */
    categories: categorySearch.categories,
    /** How many categories match the dialog's search in all, when its list is capped */
    categoryTotalCount: categorySearch.totalCount,
    isCategoriesLoading: categorySearch.isLoading,
    /** Whether the dialog's list is the previous search's while the current one loads */
    isSearchingCategories: categorySearch.isSearching,
    isCategoriesError: categorySearch.isError,
    retryCategories: categorySearch.retry,
    /** Searches the dialog's categories on the API */
    searchCategories: categorySearch.setSearch,
    /** Categories the dialog leaves out: scored or scoring. Failed ones stay addable, which is how one is retried */
    excludedCategoryIds,
    addCategories,
  };
}

/** Everything the Scoring Controls and the grid read from `useCollectionScoringControls` */
type CollectionScoringControlsState = ReturnType<typeof useCollectionScoringControls>;

export {
  toDefaultBenchmarkKey,
  toLastScoringBenchmarkKey,
  toScorableCategoryFilters,
  toScoringBenchmark,
  useCollectionScoringControls,
};
export type { CollectionScoringControlsState };
